
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { useMoveItemsMutation } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { Item } from "@/modules/mine/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";

type MoveItemResult = {
  action: "moved";
  itemId?: string;
  itemIds: string[];
  folderId: string | null;
};

interface MoveItemProps extends DialogComponentProps<MoveItemResult> {
  item?: Item;
  items?: Item[];
}

export function MoveItem({ close, resolve, item, items }: MoveItemProps) {
  const targetItems = useMemo(() => items ?? (item ? [item] : []), [item, items]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [hasSelectedFolder, setHasSelectedFolder] = useState(false);
  const [folderError, setFolderError] = useState("");
  const moveItemsMutation = useMoveItemsMutation();
  const itemCount = targetItems.length;

  return (
    <div>
      <div className="space-y-2 mb-6">
        <FolderSelector 
            returnDropdownContent 
            showSearch
            allowCreate
            allowEmpty={true}
            emptyLabel="未分类"
            value={selectedFolderId}
            emptySelected={hasSelectedFolder && selectedFolderId === null}
            onChange={(folderId) => {
              setSelectedFolderId(folderId);
              setHasSelectedFolder(true);
              setFolderError("");
            }} />
        {folderError && (
          <p className="text-sm text-destructive">{folderError}</p>
        )}
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={moveItemsMutation.isPending}
        >
          取消
        </Button>
        <Button type="button" disabled={moveItemsMutation.isPending || itemCount === 0} onClick={() => {
          if (!hasSelectedFolder) {
            setFolderError("请选择要移动到的文件夹");
            return;
          }

          const changedItems = targetItems.filter((targetItem) => (targetItem.folder_id || null) !== selectedFolderId);

          if (changedItems.length === 0) {
            close();
            return;
          }

          moveItemsMutation.mutate(
            {
              items: changedItems,
              folderId: selectedFolderId,
            },
            {
              onSuccess: (result) => {
                toast.success(itemCount > 1 ? `已移动 ${result.movedCount} 个收藏` : "移动成功");
                resolve({
                  action: "moved",
                  itemId: item?.id,
                  itemIds: result.itemIds,
                  folderId: selectedFolderId,
                });
              },
            },
          );
        }}>
          {moveItemsMutation.isPending && <Loader2 className="animate-spin" />}确认
        </Button>
      </DialogFooter>
    </div>
  );
}
